import pickBy from 'lodash.pickby'
import moment from 'moment'
import numeral from 'numeral'
import {
  ApprovalDateRangeFilterInAPI,
  DateRangeFilterInAPI,
  ID,
  MembersFilter,
  PaymentScheduleConfiguration,
  ReportsFilter,
  ReportsFilterInAPI
} from 'types/entity'
import { MILES_KM_CONVERSION_NUMBER } from 'app/settings/defaultValues'
/**
 *
 * @param obj: Any object
 * @returns a new object whose value is not null, undefined, empty string or empty array
 */
export const cleanObject = <R>(obj: Record<string, any>): NonNullable<R> => {
  const filterFn = (value: unknown) => {
    if (Array.isArray(value)) return value.length > 0
    return !(value === null || value === undefined || value === '')
  }
  return pickBy(obj, filterFn) as NonNullable<R>
}

export const objectToQueryParams = (obj: Record<string, string>) => {
  return new URLSearchParams(obj).toString()
}

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const formatDateParams = <T>(
  dateRange: string[] | undefined | null,
  name = ''
) => {
  const [start, end] = dateRange || []
  const keyName = name ?? ''
  return {
    [`${keyName}start_day`]:
      start && moment(start).isValid() ? moment(start).format('DD') : undefined,
    [`${keyName}start_month`]:
      start && moment(start).isValid() ? moment(start).format('MM') : undefined,
    [`${keyName}start_year`]:
      start && moment(start).isValid()
        ? moment(start).format('YYYY')
        : undefined,
    [`${keyName}end_day`]:
      end && moment(end).isValid() ? moment(end).format('DD') : undefined,
    [`${keyName}end_month`]:
      end && moment(end).isValid() ? moment(end).format('MM') : undefined,
    [`${keyName}end_year`]:
      end && moment(end).isValid() ? moment(end).format('YYYY') : undefined
  } as T
}

export const formatComplianceDateParams = <T>(
  date: string | undefined | null,
  name = ''
) => {
  const keyName = name ?? ''
  if (keyName === 'review_date') {
    return {
      [`review_started_at_day`]:
        date && moment(date).isValid() ? moment(date).format('DD') : undefined,
      [`review_started_at_month`]:
        date && moment(date).isValid() ? moment(date).format('MM') : undefined,
      [`review_started_at_year`]:
        date && moment(date).isValid() ? moment(date).format('YYYY') : undefined
    } as T
  }
  if (keyName === 'submission_date') {
    return {
      [`compliance_submitted_at_day`]:
        date && moment(date).isValid() ? moment(date).format('DD') : undefined,
      [`compliance_submitted_at_month`]:
        date && moment(date).isValid() ? moment(date).format('MM') : undefined,
      [`compliance_submitted_at_year`]:
        date && moment(date).isValid() ? moment(date).format('YYYY') : undefined
    } as T
  }
  return {} as T
}

export const getDateRageLabel = (
  start: string,
  end: string,
  includeYearLabel = false,
  useShortMonth = false
) => {
  if (!start || !end) return ''
  const startMoment =
    start.toLowerCase() === 'today' ? moment() : moment.utc(start)
  const endMoment = end.toLowerCase() === 'today' ? moment() : moment.utc(end)
  const startMonth = startMoment.format('M')
  const startYear = startMoment.format('Y')
  const endMonth = endMoment.format('M')
  const endYear = endMoment.format('Y')
  if (startMonth === endMonth) {
    const rangeLabel = `${startMoment.format(
      useShortMonth ? 'MMM D' : 'MMMM D'
    )} - ${endMoment.format('D')}`

    if (includeYearLabel) {
      return `${rangeLabel}, ${startMoment.format('YYYY')}`
    }

    return rangeLabel
  }

  if (includeYearLabel && startYear !== endYear) {
    return `${startMoment.format('MMM D, YYYY')} - ${endMoment.format(
      'MMM D, YYYY'
    )}`
  }

  if (includeYearLabel && startYear === endYear) {
    return `${startMoment.format('MMM D')} - ${endMoment.format(
      'MMM D'
    )}, ${startMoment.format('YYYY')}`
  }

  return `${startMoment.format('MMM D')} - ${endMoment.format('MMM D')}`
}

export const daysBetweenNowAndDate = (dateString: string): number => {
  if (!dateString) return 0
  const date = moment.utc(dateString)
  const now = moment().utc().startOf('day')
  return date.diff(now, 'd')
}

export const STATES_CODE: Record<string, string> = {
  Other: 'Non-US',
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District Of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  MP: 'Northern Mariana Islands',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming'
}

export const getFilteredStates = (exclude: string[] = []) => {
  const excludeSet = new Set(exclude)
  const allSet = new Set(Object.keys(STATES_CODE))
  excludeSet.forEach((value) => {
    if (allSet.has(value)) allSet.delete(value)
  })
  const newObj: Record<string, string> = {}
  Array.from(allSet).forEach((key) => {
    newObj[key] = STATES_CODE[key]
  })
  return newObj
}

export const pluralDay = (value: string | number) => {
  const numValue = typeof value === 'string' ? Number(value) : value

  if (numValue === 1) {
    return 'day'
  }

  return 'days'
}

export const pluralMonth = (value: string | number) => {
  const numValue = typeof value === 'string' ? Number(value) : value

  if (numValue === 1) {
    return 'month'
  }

  return 'months'
}

export const getDaysOptionsArray = (
  start: number,
  end: number
): {
  token_id: ID
  label: string
}[] => {
  const result: {
    token_id: ID
    label: string
  }[] = []
  for (let i = start; i <= end; i += 1) {
    result.push({ token_id: i, label: `${i}` })
  }
  return result
}

export const formatUSD = (number: number | string) => {
  if (number === null || number === undefined) return ''
  return numeral(number).format('$0,0.00')
}

export const getFrequencyDescription = (
  frequency?: PaymentScheduleConfiguration['frequency'],
  biweekly_day_of_week_start?: PaymentScheduleConfiguration['biweekly_day_of_week_start'],
  noFrequecy = 'No frequency'
) => {
  const dayOfWeek = capitalize(biweekly_day_of_week_start ?? 'monday')
  return frequency === 'monthly'
    ? 'Monthly, starts every 1st of the month'
    : frequency === 'first_and_sixteenth'
      ? 'Starts every 1st and 16th of the month'
      : frequency === 'weekly'
        ? `Period starts every ${dayOfWeek} from week of Jan. 2, ${new Date().getFullYear()}`
        : frequency === 'biweekly'
          ? `Period starts every other ${dayOfWeek} from week of Jan. 2, ${new Date().getFullYear()}`
          : frequency === 'biweekly_alternate'
            ? `Period starts every other ${dayOfWeek} from week of Jan. 9, ${new Date().getFullYear()}`
            : noFrequecy
}

export const sortObjectByArrayKey = (
  orderedArray: string[],
  objectToSort: Record<string, unknown>
): Record<string, unknown> => {
  const sortedObject: Record<string, unknown> = {}
  orderedArray.forEach((key) => {
    if (key in objectToSort) {
      sortedObject[key] = objectToSort[key]
    }
  })
  return sortedObject
}

export const sortArrayByOrderedArray = (
  array: string[],
  orderedArray: string[]
): string[] => {
  const sortedArray: string[] = []
  orderedArray.forEach((key) => {
    if (array.includes(key)) {
      sortedArray.push(key)
    }
  })
  return sortedArray
}

export const formatPhoneNumber = (str: string) => {
  const cleaned = ('' + str).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  return match ? `${match[1]}-${match[2]}-${match[3]}` : ''
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateCsvFromArray = (array: any[], fileName = 'data') => {
  const csvContent = 'data:text/csv;charset=utf-8,'
  const header = Object.keys(array[0]).join(',')
  const rows = array.map((row) => Object.values(row).join(','))
  const csv = header + '\n' + rows.join('\n')

  const dataUri = encodeURI(csvContent + csv)

  const link = document.createElement('a')
  link.setAttribute('href', dataUri)
  link.setAttribute('download', `${fileName}.csv`)

  link.click()
}

export const getTeamReportParams = (
  filters: ReportsFilter
): ReportsFilterInAPI => {
  const {
    submission_date_range,
    approved_date_range,
    submitter_email,
    approver_email,
    manager_email,
    ...rest
  } = filters
  const submissionDateRange = formatDateParams<DateRangeFilterInAPI>(
    submission_date_range
  )
  const approvedDateRange = formatDateParams<ApprovalDateRangeFilterInAPI>(
    approved_date_range,
    'approved_'
  )
  const managerEmail = manager_email?.toLowerCase() ?? null
  const submitterEmail = submitter_email?.toLowerCase() ?? null
  const approverEmail = approver_email?.toLowerCase() ?? null
  return {
    ...rest,
    ...approvedDateRange,
    ...submissionDateRange,
    submitter_email: submitterEmail,
    approver_email: approverEmail,
    manager_email: managerEmail
  }
}

export const toCurrency = (value: number, digits = 2) => {
  const formatter = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits
  })
  return formatter.format(value)
}

export const isObject = (item: unknown) => {
  return item && typeof item === 'object' && !Array.isArray(item)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepMerge = (target: any, ...sources: any[]): any => {
  if (!sources.length) return target
  const source = sources.shift()

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} })
        deepMerge(target[key], source[key])
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    }
  }
  return deepMerge(target, ...sources)
}

export const urlParamsToObject = (urlParams: URLSearchParams) => {
  const params: Record<string, string> = {}
  for (const [key, value] of urlParams.entries()) {
    params[key] = value
  }
  return params
}

export const pluralizeWord = ({
  singular,
  plural,
  showPlural,
  count = 0
}: {
  singular: string
  plural: string
  showPlural?: boolean
  count?: number
}): string => {
  return showPlural || count > 1 ? plural : singular
}

export const kmsRateToMilesRate = (kmsRate: number, fixed = 4) => {
  if (!kmsRate) {
    return '0'
  }
  return (kmsRate * MILES_KM_CONVERSION_NUMBER).toFixed(fixed)
}

export const milesRateToKmsRate = (milesRate: number, fixed = 4) => {
  if (!milesRate) {
    return '0'
  }
  return (milesRate / MILES_KM_CONVERSION_NUMBER).toFixed(fixed)
}

export const isValidUuid = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    str
  )

/**
 * Cleans and validates onboarding filter parameters
 * @param filters - Optional MembersFilter object containing onboarding-related filters
 * @returns A cleaned MembersFilter object with only valid onboarding parameters
 * - Removes falsy needs_* flags
 * - When onboarding is true, sets include_invites to true and archived to false
 * - Removes empty/null values using cleanObject
 */
export const cleanOnboardingParams = (
  filters?: MembersFilter
): MembersFilter => {
  const cleanFilters = { ...filters }

  if (!filters?.needs_program) {
    delete cleanFilters.needs_program
  }
  if (!filters?.needs_invite) {
    delete cleanFilters.needs_invite
  }
  if (!filters?.needs_accept_invite) {
    delete cleanFilters.needs_accept_invite
  }
  if (!filters?.needs_download_app) {
    delete cleanFilters.needs_download_app
  }
  if (!filters?.needs_direct_deposit) {
    delete cleanFilters.needs_direct_deposit
  }
  if (!filters?.onboarding) {
    delete cleanFilters.onboarding
  }
  if (filters?.onboarding) {
    cleanFilters.include_invites = true
    cleanFilters.archived = false
  }

  return cleanObject(cleanFilters ?? {})
}
